import { lazy } from 'react';
import PropTypes from 'prop-types';
import { Card, Input, RadioGroup, Stack } from '@etg/wings';
import { useProperty, useSiteContext, useTranslation } from '@eti/providers';
import { propTypes as paymentPropTypes } from '../../entry-page/components/propTypes';
import { useKlarna } from '../klarna/hooks/useKlarna';
import { reduxFields } from '../../constants/formFields';
import { paymentMethodCodes, paymentMethodTypes } from '../../constants/paymentMethods';
import PaymentHeader from '../../components/PaymentHeader';
import { getMarketingCarrierCodesList } from '../../utils/carriers';
import { getMethod } from '../../utils/paymentMethods';
import Skeleton from '../../../../common/components/Skeleton';
import PaymentMethod from './PaymentMethod';

const { SELECTED_PAYMENT_METHOD } = reduxFields;
const { BANK, PAY_INTERNAL, WALLET } = paymentMethodTypes;
const {
  AFFIRM,
  ALIPAY_CN,
  ALIPAY_HK,
  APPLE_PAY,
  APP_CARD,
  BANCONTACT,
  CARD,
  DUMMY_BANK,
  GOOGLE_PAY,
  IDEAL,
  KAKAOPAY,
  KLARNA_ONE,
  KLARNA_PAY_LATER,
  KLARNA_PAY_NOW,
  KLARNA_SLICE_IT,
  KLARNA_SOFORT,
  NAVERPAY,
  ONLINE_BANKING_PL,
  PAYPAL,
  PAYPAL_PAY_LATER,
  PIX,
  SOFORT,
  TRUSTLY,
  UPI,
  VOLT,
} = paymentMethodCodes;

const AffirmPaymentForm = lazy(() => import('../affirm/containers/AffirmPaymentFormContainer'));
const AlipayPaymentForm = lazy(() => import('../alipay/AlipayPaymentForm'));
const ApplePaymentForm = lazy(() => import('../apple-pay/containers/ApplePaymentFormContainer'));
const ApplePaymentFormV2 = lazy(
  () => import('../apple-pay/containers/ApplePaymentFormContainerV2'),
);
const BancontactPaymentForm = lazy(
  () => import('../bancontact/containers/BancontactPaymentFormContainer'),
);
const BankPaymentForm = lazy(() => import('../bank/containers/BankPaymentFormContainer'));
const CardPaymentForm = lazy(() => import('../card/container/CardPaymentFormContainer'));
const GooglePaymentForm = lazy(() => import('../google-pay/containers/GooglePayContainer'));
const IdealPaymentForm = lazy(() => import('../ideal/containers/IdealFormContainer'));
const KlarnaPaymentForm = lazy(() => import('../klarna/containers/KlarnaPaymentFormContainer'));
const OnlineBankingPlPaymentForm = lazy(
  () => import('../online_banking_pl/containers/OnlineBankingPlPaymentFormContainer'),
);
const PayInternalPaymentForm = lazy(
  () => import('../payInternal/components/PayInternalPaymentForm'),
);
const PayPalPaymentForm = lazy(() => import('../paypal/containers/PayPalPaymentFormContainer'));
const PixPaymentFormContainer = lazy(() => import('../pix/containers/PixPaymentFormContainer'));
const SofortPaymentForm = lazy(() => import('../sofort/SofortPaymentForm'));
const TrustlyPaymentForm = lazy(() => import('../trustly/TrustlyPaymentForm'));
const WalletPaymentFormContainer = lazy(
  () => import('../wallet/containers/WalletPaymentFormContainer'),
);
const UpiPaymentForm = lazy(() => import('../upi/containers/UpiPaymentFormContainer'));
const AppCardPaymentFormContainer = lazy(
  () => import('../app-card/containers/AppCardPaymentFormContainer'),
);
const AppCardPaymentForm = lazy(() => import('../app-card/form/AppCardPaymentForm'));
const DefaultPaymentForm = lazy(() => import('../default/DefaultPaymentForm'));

const PaymentMethods = ({
  bounds,
  change,
  form,
  handleSubmit,
  isLoading,
  isPanAndDoBDisabled,
  isPayInternalPayment,
  onDeclineTripChangeRef,
  onMakeOrderMutationCompleted,
  onMakeOrderMutationError,
  onPaymentResponseProblem,
  onPriceChange,
  paymentLinkCartId,
  paymentMethods = [],
  refetchCart,
  refetchPaymentQuery,
  selectedMainPaymentMethod,
  selectedPaymentMethodCode,
  setIsCFCModalOpen,
  setShouldShowLoadingDots,
  submittedWithNoPaymentMethod,
  subtotalPrice,
  transactionId,
  travelers,
  triggerPayment,
}) => {
  const { t } = useTranslation();
  const { p } = useProperty();
  const { siteCurrency } = useSiteContext();

  const carriersToShowAddressFields = p('Payment.ForceShowAddressFields.Carriers');

  const { klarnaSessionData, refetchKlarnaSession } = useKlarna(paymentMethods);

  const travelArrangerFirstName = sessionStorage.getItem('travelArrangerFirstName');
  const travelArrangerLastName = sessionStorage.getItem('travelArrangerLastName');

  const marketingCarriersList = getMarketingCarrierCodesList(bounds);
  const shouldDisplayAddressFields = marketingCarriersList.some((code) =>
    carriersToShowAddressFields?.split(',').includes(code),
  );
  const cardPaymentMethod = getMethod(paymentMethods, CARD);

  const getPaymentMethodTitle = (code, name) => {
    switch (code) {
      case APP_CARD:
        return t('Payment.Method.AppCard.Text');
      case CARD:
        return t('PaymentPage.Payment.Method.ButtonText.Card');
      case KLARNA_PAY_LATER:
        return t('Payment.Method.Klarna.PayLater.Text');
      case KLARNA_PAY_NOW:
        return t('Payment.Method.Klarna.PayNow.Text');
      case KLARNA_SLICE_IT:
        return t('Payment.Method.Klarna.SliceIt.Text');
      case KLARNA_SOFORT:
        return t('Payment.Method.Klarna.Sofortuberweisung.Text');
      case UPI:
        return t('Payment.Method.Upi.Text');
      case VOLT:
        return t('Payment.Method.Volt.Text');
      default:
        return t(`Payment.Method.${code}.Label`) || name;
    }
  };

  const getPaymentFormForCode = (code) => {
    switch (code) {
      case AFFIRM:
        return (
          <AffirmPaymentForm
            handleSubmit={handleSubmit}
            onMakeOrderMutationError={onMakeOrderMutationError}
            onPaymentResponseProblem={onPaymentResponseProblem}
            onPriceChange={onPriceChange}
            paymentLinkCartId={paymentLinkCartId}
            refetchCart={refetchCart}
            refetchPaymentQuery={refetchPaymentQuery}
            transactionId={transactionId}
            travelArrangerFirstName={travelArrangerFirstName}
            travelArrangerLastName={travelArrangerLastName}
            travelers={travelers}
            triggerPayment={triggerPayment}
          />
        );
      case ALIPAY_CN:
      case ALIPAY_HK:
        return (
          <WalletPaymentFormContainer
            handleSubmit={handleSubmit}
            onMakeOrderMutationError={onMakeOrderMutationError}
            onPaymentResponseProblem={onPaymentResponseProblem}
            onPriceChange={onPriceChange}
            paymentForm={<AlipayPaymentForm code={code} />}
            paymentLinkCartId={paymentLinkCartId}
            refetchCart={refetchCart}
            refetchPaymentQuery={refetchPaymentQuery}
            selectedPaymentMethod={selectedMainPaymentMethod}
            transactionId={transactionId}
            travelArrangerFirstName={travelArrangerFirstName}
            travelArrangerLastName={travelArrangerLastName}
            travelers={travelers}
            triggerPayment={triggerPayment}
          />
        );
      case APPLE_PAY:
        if (p('Payment.Method.ApplePay.SDK.Enabled')) {
          return (
            <ApplePaymentFormV2
              additionalData={getMethod(paymentMethods, APPLE_PAY)?.additionalData || null}
              handleSubmit={handleSubmit}
              isPanAndDoBDisabled={isPanAndDoBDisabled}
              onMakeOrderMutationError={onMakeOrderMutationError}
              onPaymentResponseProblem={onPaymentResponseProblem}
              onPriceChange={onPriceChange}
              paymentLinkCartId={paymentLinkCartId}
              refetchCart={refetchCart}
              refetchPaymentQuery={refetchPaymentQuery}
              shouldDisplayAddressFields={shouldDisplayAddressFields}
              transactionId={transactionId}
              travelArrangerFirstName={travelArrangerFirstName}
              travelArrangerLastName={travelArrangerLastName}
              travelers={travelers}
              triggerPayment={triggerPayment}
            />
          );
        }

        return (
          <ApplePaymentForm
            additionalData={getMethod(paymentMethods, APPLE_PAY)?.additionalData || null}
            handleSubmit={handleSubmit}
            isPanAndDoBDisabled={isPanAndDoBDisabled}
            onMakeOrderMutationError={onMakeOrderMutationError}
            onPaymentResponseProblem={onPaymentResponseProblem}
            onPriceChange={onPriceChange}
            paymentLinkCartId={paymentLinkCartId}
            refetchCart={refetchCart}
            refetchPaymentQuery={refetchPaymentQuery}
            shouldDisplayAddressFields={shouldDisplayAddressFields}
            transactionId={transactionId}
            travelArrangerFirstName={travelArrangerFirstName}
            travelArrangerLastName={travelArrangerLastName}
            travelers={travelers}
            triggerPayment={triggerPayment}
          />
        );
      case DUMMY_BANK:
        return (
          <BankPaymentForm
            handleSubmit={handleSubmit}
            onMakeOrderMutationError={onMakeOrderMutationError}
            onPaymentResponseProblem={onPaymentResponseProblem}
            onPriceChange={onPriceChange}
            paymentLinkCartId={paymentLinkCartId}
            paymentMethods={paymentMethods}
            refetchCart={refetchCart}
            refetchPaymentQuery={refetchPaymentQuery}
            selectedPaymentMethod={selectedMainPaymentMethod}
            travelArrangerFirstName={travelArrangerFirstName}
            travelArrangerLastName={travelArrangerLastName}
            travelers={travelers}
            triggerPayment={triggerPayment}
          />
        );
      case IDEAL:
        return (
          <IdealPaymentForm
            change={change}
            handleSubmit={handleSubmit}
            onMakeOrderMutationError={onMakeOrderMutationError}
            onPaymentResponseProblem={onPaymentResponseProblem}
            onPriceChange={onPriceChange}
            paymentLinkCartId={paymentLinkCartId}
            refetchCart={refetchCart}
            refetchPaymentQuery={refetchPaymentQuery}
            selectedPaymentMethod={selectedMainPaymentMethod}
            transactionId={transactionId}
            travelArrangerFirstName={travelArrangerFirstName}
            travelArrangerLastName={travelArrangerLastName}
            travelers={travelers}
            triggerPayment={triggerPayment}
          />
        );
      case CARD:
        return (
          <CardPaymentForm
            cardPaymentMethod={cardPaymentMethod}
            handleSubmit={handleSubmit}
            isPanAndDoBDisabled={isPanAndDoBDisabled}
            onMakeOrderMutationError={onMakeOrderMutationError}
            onPaymentResponseProblem={onPaymentResponseProblem}
            onPriceChange={onPriceChange}
            paymentLinkCartId={paymentLinkCartId}
            refetchCart={refetchCart}
            refetchPaymentQuery={refetchPaymentQuery}
            shouldDisplayAddressFields={shouldDisplayAddressFields}
            transactionId={transactionId}
            travelArrangerFirstName={travelArrangerFirstName}
            travelArrangerLastName={travelArrangerLastName}
            travelers={travelers}
            triggerPayment={triggerPayment}
          />
        );
      case GOOGLE_PAY:
        return (
          <GooglePaymentForm
            additionalData={getMethod(paymentMethods, GOOGLE_PAY)?.additionalData || null}
            form={form}
            handleSubmit={handleSubmit}
            isPanAndDoBDisabled={isPanAndDoBDisabled}
            onMakeOrderMutationError={onMakeOrderMutationError}
            onPaymentResponseProblem={onPaymentResponseProblem}
            onPriceChange={onPriceChange}
            paymentLinkCartId={paymentLinkCartId}
            refetchCart={refetchCart}
            refetchPaymentQuery={refetchPaymentQuery}
            transactionId={transactionId}
            travelArrangerFirstName={travelArrangerFirstName}
            travelArrangerLastName={travelArrangerLastName}
            travelers={travelers}
            triggerPayment={triggerPayment}
          />
        );
      case KLARNA_ONE:
      case KLARNA_PAY_LATER:
      case KLARNA_PAY_NOW:
      case KLARNA_SLICE_IT:
      case KLARNA_SOFORT:
        return (
          <KlarnaPaymentForm
            handleSubmit={handleSubmit}
            klarnaSessionData={klarnaSessionData}
            onDeclineTripChangeRef={onDeclineTripChangeRef}
            onMakeOrderMutationError={onMakeOrderMutationError}
            onPaymentResponseProblem={onPaymentResponseProblem}
            onPriceChange={onPriceChange}
            paymentLinkCartId={paymentLinkCartId}
            refetchCart={refetchCart}
            refetchKlarnaSession={refetchKlarnaSession}
            refetchPaymentQuery={refetchPaymentQuery}
            selectedPaymentMethod={selectedMainPaymentMethod}
            transactionId={transactionId}
            travelArrangerFirstName={travelArrangerFirstName}
            travelArrangerLastName={travelArrangerLastName}
            travelers={travelers}
            triggerPayment={triggerPayment}
          />
        );
      case ONLINE_BANKING_PL:
        return (
          <OnlineBankingPlPaymentForm
            change={change}
            handleSubmit={handleSubmit}
            onMakeOrderMutationError={onMakeOrderMutationError}
            onPaymentResponseProblem={onPaymentResponseProblem}
            onPriceChange={onPriceChange}
            paymentLinkCartId={paymentLinkCartId}
            refetchCart={refetchCart}
            refetchPaymentQuery={refetchPaymentQuery}
            selectedPaymentMethod={selectedMainPaymentMethod}
            shouldDisplayAddressFields={shouldDisplayAddressFields}
            transactionId={transactionId}
            travelArrangerFirstName={travelArrangerFirstName}
            travelArrangerLastName={travelArrangerLastName}
            travelers={travelers}
            triggerPayment={triggerPayment}
          />
        );
      case PAYPAL:
        return (
          <PayPalPaymentForm
            currencyExponent={siteCurrency.exponent}
            handleSubmit={handleSubmit}
            isPanAndDoBDisabled={isPanAndDoBDisabled}
            onMakeOrderMutationError={onMakeOrderMutationError}
            onPaymentResponseProblem={onPaymentResponseProblem}
            onPriceChange={onPriceChange}
            paymentLinkCartId={paymentLinkCartId}
            paymentMethodCode={PAYPAL}
            refetchCart={refetchCart}
            refetchPaymentQuery={refetchPaymentQuery}
            setIsCFCModalOpen={setIsCFCModalOpen}
            setShouldShowLoadingDots={setShouldShowLoadingDots}
            transactionId={transactionId}
            travelArrangerFirstName={travelArrangerFirstName}
            travelArrangerLastName={travelArrangerLastName}
            travelers={travelers}
            triggerPayment={triggerPayment}
          />
        );
      case PAYPAL_PAY_LATER:
        return (
          <PayPalPaymentForm
            currencyExponent={siteCurrency.exponent}
            handleSubmit={handleSubmit}
            isPanAndDoBDisabled={isPanAndDoBDisabled}
            onMakeOrderMutationError={onMakeOrderMutationError}
            onPaymentResponseProblem={onPaymentResponseProblem}
            onPriceChange={onPriceChange}
            paymentLinkCartId={paymentLinkCartId}
            paymentMethodCode={PAYPAL_PAY_LATER}
            refetchCart={refetchCart}
            refetchPaymentQuery={refetchPaymentQuery}
            setIsCFCModalOpen={setIsCFCModalOpen}
            setShouldShowLoadingDots={setShouldShowLoadingDots}
            transactionId={transactionId}
            travelArrangerFirstName={travelArrangerFirstName}
            travelArrangerLastName={travelArrangerLastName}
            travelers={travelers}
            triggerPayment={triggerPayment}
          />
        );
      case PAY_INTERNAL:
        return (
          <PayInternalPaymentForm
            handleSubmit={handleSubmit}
            onMakeOrderMutationError={onMakeOrderMutationError}
            onPaymentResponseProblem={onPaymentResponseProblem}
            onPriceChange={onPriceChange}
            paymentLinkCartId={paymentLinkCartId}
            refetchPaymentQuery={refetchPaymentQuery}
            transactionId={transactionId}
            travelArrangerFirstName={travelArrangerFirstName}
            travelArrangerLastName={travelArrangerLastName}
            travelers={travelers}
            triggerPayment={triggerPayment}
          />
        );
      case TRUSTLY:
        return (
          <WalletPaymentFormContainer
            handleSubmit={handleSubmit}
            onMakeOrderMutationError={onMakeOrderMutationError}
            onPaymentResponseProblem={onPaymentResponseProblem}
            onPriceChange={onPriceChange}
            paymentForm={
              <TrustlyPaymentForm shouldDisplayAddressFields={shouldDisplayAddressFields} />
            }
            paymentLinkCartId={paymentLinkCartId}
            refetchCart={refetchCart}
            refetchPaymentQuery={refetchPaymentQuery}
            selectedPaymentMethod={selectedMainPaymentMethod}
            transactionId={transactionId}
            travelArrangerFirstName={travelArrangerFirstName}
            travelArrangerLastName={travelArrangerLastName}
            travelers={travelers}
            triggerPayment={triggerPayment}
          />
        );
      case SOFORT:
        return (
          <WalletPaymentFormContainer
            handleSubmit={handleSubmit}
            onMakeOrderMutationError={onMakeOrderMutationError}
            onPaymentResponseProblem={onPaymentResponseProblem}
            onPriceChange={onPriceChange}
            paymentForm={
              <SofortPaymentForm shouldDisplayAddressFields={shouldDisplayAddressFields} />
            }
            paymentLinkCartId={paymentLinkCartId}
            refetchCart={refetchCart}
            refetchPaymentQuery={refetchPaymentQuery}
            selectedPaymentMethod={selectedMainPaymentMethod}
            transactionId={transactionId}
            travelArrangerFirstName={travelArrangerFirstName}
            travelArrangerLastName={travelArrangerLastName}
            travelers={travelers}
            triggerPayment={triggerPayment}
          />
        );
      case PIX:
        return (
          <PixPaymentFormContainer
            handleSubmit={handleSubmit}
            isPanAndDoBDisabled={isPanAndDoBDisabled}
            onMakeOrderMutationError={onMakeOrderMutationError}
            onPaymentResponseProblem={onPaymentResponseProblem}
            onPriceChange={onPriceChange}
            paymentLinkCartId={paymentLinkCartId}
            refetchCart={refetchCart}
            refetchPaymentQuery={refetchPaymentQuery}
            selectedPaymentMethod={selectedMainPaymentMethod}
            shouldDisplayAddressFields={shouldDisplayAddressFields}
            transactionId={transactionId}
            travelArrangerFirstName={travelArrangerFirstName}
            travelArrangerLastName={travelArrangerLastName}
            travelers={travelers}
            triggerPayment={triggerPayment}
          />
        );
      case BANCONTACT:
        return (
          <BancontactPaymentForm
            handleSubmit={handleSubmit}
            onMakeOrderMutationError={onMakeOrderMutationError}
            onPaymentResponseProblem={onPaymentResponseProblem}
            onPriceChange={onPriceChange}
            paymentLinkCartId={paymentLinkCartId}
            refetchCart={refetchCart}
            refetchPaymentQuery={refetchPaymentQuery}
            selectedPaymentMethod={selectedMainPaymentMethod}
            transactionId={transactionId}
            travelArrangerFirstName={travelArrangerFirstName}
            travelArrangerLastName={travelArrangerLastName}
            travelers={travelers}
            triggerPayment={triggerPayment}
          />
        );
      case UPI:
        return (
          <UpiPaymentForm
            change={change}
            handleSubmit={handleSubmit}
            isPanAndDoBDisabled={isPanAndDoBDisabled}
            onMakeOrderMutationError={onMakeOrderMutationError}
            onPaymentResponseProblem={onPaymentResponseProblem}
            onPriceChange={onPriceChange}
            options={getMethod(paymentMethods, UPI)?.options}
            paymentLinkCartId={paymentLinkCartId}
            refetchCart={refetchCart}
            refetchPaymentQuery={refetchPaymentQuery}
            shouldDisplayAddressFields={shouldDisplayAddressFields}
            transactionId={transactionId}
            travelArrangerFirstName={travelArrangerFirstName}
            travelArrangerLastName={travelArrangerLastName}
            travelers={travelers}
            triggerPayment={triggerPayment}
          />
        );
      case APP_CARD:
        return (
          <AppCardPaymentFormContainer
            handleSubmit={handleSubmit}
            onMakeOrderMutationCompleted={onMakeOrderMutationCompleted}
            onMakeOrderMutationError={onMakeOrderMutationError}
            onPaymentResponseProblem={onPaymentResponseProblem}
            onPriceChange={onPriceChange}
            paymentForm={
              <AppCardPaymentForm
                options={getMethod(paymentMethods, APP_CARD)?.options}
                subtotalPrice={subtotalPrice}
              />
            }
            paymentLinkCartId={paymentLinkCartId}
            refetchCart={refetchCart}
            refetchPaymentQuery={refetchPaymentQuery}
            transactionId={transactionId}
            travelArrangerFirstName={travelArrangerFirstName}
            travelArrangerLastName={travelArrangerLastName}
            travelers={travelers}
            triggerPayment={triggerPayment}
          />
        );
      case KAKAOPAY:
      case NAVERPAY:
        return (
          <AppCardPaymentFormContainer
            handleSubmit={handleSubmit}
            onMakeOrderMutationCompleted={onMakeOrderMutationCompleted}
            onMakeOrderMutationError={onMakeOrderMutationError}
            onPaymentResponseProblem={onPaymentResponseProblem}
            onPriceChange={onPriceChange}
            paymentForm={<DefaultPaymentForm code={code} />}
            paymentLinkCartId={paymentLinkCartId}
            refetchCart={refetchCart}
            refetchPaymentQuery={refetchPaymentQuery}
            selectedPaymentMethod={selectedMainPaymentMethod}
            transactionId={transactionId}
            travelArrangerFirstName={travelArrangerFirstName}
            travelArrangerLastName={travelArrangerLastName}
            travelers={travelers}
            triggerPayment={triggerPayment}
          />
        );
      default:
        return (
          <WalletPaymentFormContainer
            handleSubmit={handleSubmit}
            onMakeOrderMutationError={onMakeOrderMutationError}
            onPaymentResponseProblem={onPaymentResponseProblem}
            onPriceChange={onPriceChange}
            paymentForm={<DefaultPaymentForm code={code} />}
            paymentLinkCartId={paymentLinkCartId}
            refetchCart={refetchCart}
            refetchPaymentQuery={refetchPaymentQuery}
            selectedPaymentMethod={selectedMainPaymentMethod}
            transactionId={transactionId}
            travelArrangerFirstName={travelArrangerFirstName}
            travelArrangerLastName={travelArrangerLastName}
            travelers={travelers}
            triggerPayment={triggerPayment}
          />
        );
    }
  };

  const handleOnChange = (event) => {
    const selectedMethodCode = event.target.value;
    const { code, id, type } = getMethod(paymentMethods, selectedMethodCode);
    change(SELECTED_PAYMENT_METHOD, {
      code,
      type,
      ...((type === WALLET || type === BANK) && { id }),
    });
  };

  const hasNotSelectedPaymentMethod = submittedWithNoPaymentMethod && !selectedPaymentMethodCode;

  return (
    <Card
      aria-busy={isLoading}
      aria-label={isLoading ? t('ScreenReader.Loading.AriaLabel.Text') : undefined}
      role={isLoading ? 'status' : undefined}
    >
      {isLoading ? (
        <Stack spacing={16}>
          <Skeleton.Box height={25} width={50} />
          {p('Payment.PaymentSeals.V2.Enabled') && <Skeleton.Box height={40} width={25} />}
          <Skeleton.Box height={60 * Math.ceil(paymentMethods.length / 2)} />
        </Stack>
      ) : (
        <Stack spacing={16}>
          <PaymentHeader
            isPayInternalPayment={isPayInternalPayment}
            paymentMethodsSize={paymentMethods.length}
          />
          <Input.Meta
            error={t('PaymentPage.Payment.Method.Header.Text')}
            errorId={`${SELECTED_PAYMENT_METHOD}-error`}
            hasError={hasNotSelectedPaymentMethod}
          />
          <RadioGroup
            errorId={`${SELECTED_PAYMENT_METHOD}-error`}
            hasError={hasNotSelectedPaymentMethod}
            name={SELECTED_PAYMENT_METHOD}
            onChange={handleOnChange}
            selectedValue={selectedMainPaymentMethod?.code}
          >
            <Stack spacing={4}>
              {paymentMethods.map((method) => (
                <PaymentMethod
                  {...method}
                  key={`${method.code}-${method.paymentServiceProvider}`}
                  paymentForm={getPaymentFormForCode(method.code)}
                  paymentMethodTitle={getPaymentMethodTitle(method.code, method.name)}
                  selectedMainPaymentMethod={selectedMainPaymentMethod?.code}
                />
              ))}
            </Stack>
          </RadioGroup>
        </Stack>
      )}
    </Card>
  );
};

PaymentMethods.propTypes = {
  ...paymentPropTypes,
  isLoading: PropTypes.bool,
  isPanAndDoBDisabled: PropTypes.bool,
  isPayInternalPayment: PropTypes.bool,
  paymentMethodPrices: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      price: PropTypes.number,
      serviceFee: PropTypes.number,
      type: PropTypes.string,
    }),
  ),
  setShouldShowLoadingDots: PropTypes.func,
};

export default PaymentMethods;
